// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Bootstraps the app, owns the small state machine (mode + idle/measuring), and
 * drives the numbers from the orientation sensor into the UI. No heavy maths
 * lives here — that is in geometry.ts, convert.ts and the pure cores.
 */

import './styles/main.css';
import {
  renderApp,
  showToast,
  hideGlossary,
  closeModal,
  isModalOpen,
  setModalOpenHook,
} from './ui';
import { mountEventDrawer, log } from './eventlog';
import {
  OrientationCapture,
  requestOrientationPermission,
  isOrientationSupported,
  needsPermission,
  getScreenAngle,
  type GravitySample,
} from './orientation';
import type { Vec3, Tilt, Calibration } from './geometry';
import {
  tiltFromGravity,
  rotateGravityForScreen,
  applyCalibration,
  NO_CALIBRATION,
} from './geometry';
import { degToGrade, roofPitchLabel, ratioLabel, rampVerdict, isConvertible } from './convert';
import { formatDeg, formatSignedDeg, formatGrade, describeLevel, summaryLine, formatClock } from './format';
import { drawBubble, drawDial, renderReportCard } from './measure';
import { PhotoProtractor } from './photo';
import type { Measurement, MeasureMode } from './types';
import { toCsv } from './csv';
import { buildPdfBytes } from './pdf';

const TOL = 0.4; // degrees within which a surface reads "level"
// v2: tiltX/tiltY changed meaning when the near-vertical singularity was fixed,
// so a zero stored by an older build would be subtracted from a different frame.
const CAL_KEY = 'tiltwell.cal.v2';
const MODE_KEY = 'tiltwell.mode';

const root = document.getElementById('app')!;
const refs = renderApp(root);
const protractor = new PhotoProtractor(refs.photoCanvas);

let mode: MeasureMode = 'level';
let measuring = false;
let latestGravity: Vec3 | null = null;
let capture: OrientationCapture | null = null;
let renderTimer: ReturnType<typeof setInterval> | null = null;
let cal: Calibration = { ...NO_CALIBRATION };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let wakeLock: any = null;

let measurements: Measurement[] = [];
let capturesRevealed = false;
let idCounter = 0;

// ── Persistence ──────────────────────────────────────────────────────

try {
  const savedCal = localStorage.getItem(CAL_KEY);
  if (savedCal) cal = { ...NO_CALIBRATION, ...JSON.parse(savedCal) };
  const savedMode = localStorage.getItem(MODE_KEY);
  if (savedMode === 'level' || savedMode === 'angle' || savedMode === 'photo') mode = savedMode;
} catch {
  /* ignore corrupt storage */
}

function persist(): void {
  try {
    localStorage.setItem(CAL_KEY, JSON.stringify(cal));
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

// ── Mode ─────────────────────────────────────────────────────────────

function setMode(next: MeasureMode): void {
  if (next === mode) return;
  // Leaving a live sensor mode for the photo tab: release the sensor.
  if (next === 'photo' && measuring) stopMeasuring();
  mode = next;
  refs.tabs.querySelectorAll<HTMLElement>('.tab').forEach((t) => {
    const on = t.dataset.mode === next;
    t.classList.toggle('active', on);
    t.setAttribute('aria-selected', String(on));
  });
  const sensorMode = next === 'level' || next === 'angle';
  refs.instrumentSection.hidden = !sensorMode;
  refs.photoSection.hidden = next !== 'photo';
  refs.resetCalBtn.hidden = !(sensorMode && (cal.x || cal.y || cal.angle));
  if (sensorMode) {
    refs.controlsNote.innerHTML =
      next === 'level'
        ? 'Lay the phone flat on a surface — or stand it upright against a wall or door frame — and press start. Use <strong>Calibrate</strong> to zero it against whatever you are checking, then the reading is how far off that reference you are.'
        : 'Hold the phone against a slope — its top edge along the incline — and press start to read the angle, grade and roof pitch.';
    renderLive();
  }
  persist();
  log.system(`Mode: ${next}.`);
}

// ── Sensor lifecycle ─────────────────────────────────────────────────

async function startMeasuring(): Promise<void> {
  if (measuring) return;
  if (!isOrientationSupported()) {
    log.sensor('No orientation sensor on this device — use the Photo tab.', 'warn');
    showToast('No sensor here — try the Photo tab.');
    setMode('photo');
    return;
  }

  refs.startBtn.disabled = true;
  const perm = await requestOrientationPermission();
  refs.startBtn.disabled = false;

  if (perm === 'denied') {
    log.sensor('Orientation access was denied. Measure an angle in a photo instead.', 'warn');
    showToast('Access denied — use the Photo tab.');
    setMode('photo');
    return;
  }
  if (perm === 'error' || perm === 'unsupported') {
    log.sensor('Could not start the orientation sensor. Use the Photo tab.', 'warn');
    showToast('Could not start the sensor — use the Photo tab.');
    setMode('photo');
    return;
  }

  capture = new OrientationCapture({
    onSample: (s: GravitySample) => {
      latestGravity = s.gravity;
    },
    onNoData: () => {
      if (measuring) {
        log.sensor('The sensor is not reporting orientation. On a desktop, use the Photo tab.', 'warn');
        showToast('No tilt detected — is this a desktop? Use Photo.');
      }
    },
  });
  capture.start();
  measuring = true;
  setMeasuringUi(true);
  void acquireWakeLock();
  startRenderLoop();
  log.sensor(needsPermission() ? 'Orientation access granted — measuring.' : 'Listening to the orientation sensor.', 'ok');
}

function stopMeasuring(): void {
  capture?.stop();
  capture = null;
  measuring = false;
  latestGravity = null;
  stopRenderLoop();
  releaseWakeLock();
  setMeasuringUi(false);
  refs.readoutStatus.textContent = 'press start';
  refs.readoutStatus.dataset.tone = 'idle';
  refs.liveTag.hidden = true;
  log.sensor('Stopped. The orientation sensor is released.', 'ok');
}

function setMeasuringUi(on: boolean): void {
  refs.startBtn.hidden = on;
  refs.calBtn.hidden = !on;
  refs.captureBtn.hidden = !on;
  refs.stopBtn.hidden = !on;
  refs.liveTag.hidden = !on;
  refs.resetCalBtn.hidden = !(cal.x || cal.y || cal.angle);
}

function startRenderLoop(): void {
  stopRenderLoop();
  // A hidden tab throttles requestAnimationFrame to a halt, so the live view is
  // driven by setInterval — it keeps ticking in the background instead of hanging.
  renderTimer = setInterval(renderLive, 1000 / 30);
}

function stopRenderLoop(): void {
  if (renderTimer !== null) {
    clearInterval(renderTimer);
    renderTimer = null;
  }
}

// ── Wake lock (best effort) ──────────────────────────────────────────

async function acquireWakeLock(): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wl = (navigator as any).wakeLock;
    if (wl?.request) wakeLock = await wl.request('screen');
  } catch {
    /* ignore — non-essential */
  }
}

function releaseWakeLock(): void {
  try {
    wakeLock?.release?.();
  } catch {
    /* ignore */
  }
  wakeLock = null;
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && measuring && !wakeLock) void acquireWakeLock();
});

// ── Live render ──────────────────────────────────────────────────────

/** The current calibrated tilt, or null if no reading has arrived yet. */
function currentTilt(): Tilt | null {
  if (!latestGravity) return null;
  const g = rotateGravityForScreen(latestGravity, getScreenAngle());
  return applyCalibration(tiltFromGravity(g), cal);
}

/** The angle the conversions apply to for the active sensor mode. */
function primaryAngle(t: Tilt): number {
  return mode === 'angle' ? Math.abs(t.longAxisFromHorizontal) : t.fromHorizontal;
}

function renderLive(): void {
  if (mode === 'photo') return;
  const t = currentTilt();
  if (!t) {
    if (mode === 'level') drawBubble(refs.gauge, 0, 0, TOL);
    else drawDial(refs.gauge, 0);
    setConversions(null);
    return;
  }
  const angle = primaryAngle(t);
  refs.readoutMain.textContent = formatDeg(angle);

  if (mode === 'level') {
    const level = t.fromHorizontal <= TOL;
    refs.readoutStatus.textContent = level ? 'LEVEL' : describeLevel(t.fromHorizontal, TOL);
    refs.readoutStatus.dataset.tone = level ? 'good' : 'warn';
    refs.readoutSub.textContent = `X ${formatSignedDeg(t.tiltX)}  ·  Y ${formatSignedDeg(t.tiltY)}`;
    drawBubble(refs.gauge, t.tiltX, t.tiltY, TOL);
  } else {
    refs.readoutStatus.textContent = `${(90 - angle).toFixed(1)}° from vertical`;
    refs.readoutStatus.dataset.tone = 'idle';
    refs.readoutSub.textContent = 'slope from horizontal';
    drawDial(refs.gauge, angle);
  }
  setConversions(angle);
}

function setConversions(angle: number | null): void {
  if (angle === null || !isConvertible(angle)) {
    refs.convGrade.textContent = '—';
    refs.convPitch.textContent = '—';
    refs.convRatio.textContent = '—';
    refs.rampVerdict.textContent = angle === null ? '' : 'Too steep for a grade, roof pitch or ramp ratio.';
    refs.rampVerdict.dataset.level = 'steep';
    return;
  }
  refs.convGrade.textContent = formatGrade(degToGrade(angle));
  refs.convPitch.textContent = roofPitchLabel(angle);
  refs.convRatio.textContent = ratioLabel(angle);
  const v = rampVerdict(angle);
  refs.rampVerdict.textContent = `Ramp: ${v.label} (${v.ratio}). Common accessibility guidance — not code advice.`;
  refs.rampVerdict.dataset.level = v.level;
}

// ── Calibration ──────────────────────────────────────────────────────

function calibrate(): void {
  if (!latestGravity) {
    showToast('Wait for a reading before calibrating.');
    return;
  }
  const g = rotateGravityForScreen(latestGravity, getScreenAngle());
  const raw = tiltFromGravity(g);
  if (mode === 'level') {
    // Store the raw per-axis tilt so "here" becomes the new zero.
    cal = { ...cal, x: raw.tiltX, y: raw.tiltY };
  } else {
    cal = { ...cal, angle: raw.longAxisFromHorizontal };
  }
  persist();
  refs.resetCalBtn.hidden = false;
  showToast(mode === 'level' ? 'Zeroed to this surface.' : 'Zeroed to this angle.');
  log.sensor('Calibrated — current position is now zero.', 'ok');
  renderLive();
}

function resetCalibration(): void {
  cal = { ...NO_CALIBRATION };
  persist();
  refs.resetCalBtn.hidden = true;
  showToast('Calibration cleared.');
  log.sensor('Calibration reset to true horizontal.', 'ok');
  renderLive();
}

// ── Captures ─────────────────────────────────────────────────────────

function buildMeasurement(m: MeasureMode, angle: number, opts: { tiltX?: number; tiltY?: number; convert?: boolean } = {}): Measurement {
  const convert = opts.convert ?? true;
  const label =
    m === 'level' ? 'Tilt' : m === 'angle' ? 'Slope' : 'Angle';
  return {
    id: ++idCounter,
    label: `${label} ${measurements.length + 1}`,
    mode: m,
    angle,
    tiltX: opts.tiltX,
    tiltY: opts.tiltY,
    grade: convert ? degToGrade(angle) : NaN,
    roofPitch: convert ? roofPitchLabel(angle) : '—',
    ratio: convert ? ratioLabel(angle) : '—',
    at: Date.now(),
  };
}

function addMeasurement(m: Measurement): void {
  measurements.unshift(m);
  if (!capturesRevealed) {
    capturesRevealed = true;
    refs.capturesSection.hidden = false;
  }
  renderCaptureList();
  refs.sharePngBtn.hidden = !('share' in navigator && 'canShare' in navigator);
  log.capture(`Captured ${m.label}: ${formatDeg(m.angle)}.`, 'ok');
}

function captureCurrent(): void {
  const t = currentTilt();
  if (!t) {
    showToast('No live reading to capture yet.');
    return;
  }
  const angle = primaryAngle(t);
  const m =
    mode === 'level'
      ? buildMeasurement('level', angle, { tiltX: t.tiltX, tiltY: t.tiltY })
      : buildMeasurement('angle', angle);
  addMeasurement(m);
  showToast(`Captured ${formatDeg(angle)}.`);
}

function capturePhoto(): void {
  const info = protractor.measurement();
  if (!info) {
    showToast('Tap points on the photo first.');
    return;
  }
  const convert = info.kind === 'slope' && info.angle <= 89.9;
  addMeasurement(buildMeasurement('photo', info.angle, { convert }));
  showToast(`Captured ${formatDeg(info.angle)}.`);
}

function renderCaptureList(): void {
  refs.capturesEmpty.hidden = measurements.length > 0;
  refs.captureList.innerHTML = '';
  for (const m of measurements) {
    const row = document.createElement('div');
    row.className = 'capture-row';
    const extra = Number.isFinite(m.grade) ? `${formatGrade(m.grade)} · ${m.roofPitch} · ${m.ratio}` : m.mode === 'photo' ? 'vertex angle' : '';
    row.innerHTML = `
      <span class="cap-angle">${formatDeg(m.angle)}</span>
      <span class="cap-meta"><strong>${escapeHtml(m.label)}</strong><span>${escapeHtml(extra)}</span></span>
      <span class="cap-time">${formatClock(m.at)}</span>
      <button type="button" class="cap-del" aria-label="Delete this measurement" data-id="${m.id}">&times;</button>`;
    row.querySelector('.cap-del')?.addEventListener('click', () => {
      measurements = measurements.filter((x) => x.id !== m.id);
      renderCaptureList();
      log.capture('Deleted a measurement.');
    });
    refs.captureList.appendChild(row);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

// ── Exports ──────────────────────────────────────────────────────────

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

/** The same moment, readable, for the face of an export. */
function dateStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${formatClock(d.getTime())}`;
}

// The anchor is put in the document before it is clicked: a detached <a download>
// is unreliable in Safari, which is where most people meet this tool.
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Hand a file to the OS share sheet — on iOS the only route to Photos/Files. */
async function shareFile(file: File, title: string): Promise<boolean> {
  try {
    if (!navigator.canShare?.({ files: [file] })) {
      showToast('Sharing not supported here.');
      return false;
    }
    await navigator.share({ files: [file], title });
    return true;
  } catch {
    /* user cancelled */
    return false;
  }
}

async function getReportPng(): Promise<Blob | null> {
  if (!measurements.length) return null;
  const latest = measurements[0];
  const sub = Number.isFinite(latest.grade)
    ? `${formatGrade(latest.grade)} · ${latest.roofPitch} · ${latest.ratio}`
    : latest.mode === 'photo'
      ? 'vertex angle'
      : '';
  try {
    return await renderReportCard(measurements, { headline: formatDeg(latest.angle), sub });
  } catch {
    log.export('Could not render the report image.', 'err');
    return null;
  }
}

// ── Wire controls ────────────────────────────────────────────────────

refs.tabs.querySelectorAll<HTMLElement>('.tab').forEach((t) => {
  t.addEventListener('click', () => setMode(t.dataset.mode as MeasureMode));
});

refs.startBtn.addEventListener('click', () => void startMeasuring());
refs.stopBtn.addEventListener('click', stopMeasuring);
refs.calBtn.addEventListener('click', calibrate);
refs.resetCalBtn.addEventListener('click', resetCalibration);
refs.captureBtn.addEventListener('click', captureCurrent);

// Photo input
refs.dropZone.addEventListener('click', () => refs.fileInput.click());
refs.dropZone.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    refs.fileInput.click();
  }
});
refs.fileInput.addEventListener('change', () => {
  const f = refs.fileInput.files?.[0];
  if (f) void loadPhoto(f);
  refs.fileInput.value = '';
});
['dragenter', 'dragover'].forEach((ev) =>
  refs.dropZone.addEventListener(ev, (e) => {
    e.preventDefault();
    refs.dropZone.classList.add('drag');
  }),
);
['dragleave', 'drop'].forEach((ev) =>
  refs.dropZone.addEventListener(ev, (e) => {
    e.preventDefault();
    if (ev === 'dragleave' && refs.dropZone.contains(e.target as Node)) return;
    refs.dropZone.classList.remove('drag');
  }),
);
refs.dropZone.addEventListener('drop', (e) => {
  const f = e.dataTransfer?.files?.[0];
  if (f) void loadPhoto(f);
});
refs.sampleBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  void loadSample();
});

// Press near an existing point to drag it, anywhere else to place a new one.
// Pointer capture keeps the drag tracking if the finger leaves the canvas.
refs.photoCanvas.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  if (protractor.grabOrAdd(e.clientX, e.clientY, e.pointerId)) {
    refs.photoCanvas.setPointerCapture(e.pointerId);
    refs.photoCanvas.classList.add('dragging');
  }
  updatePhotoReadout();
});
refs.photoCanvas.addEventListener('pointermove', (e) => {
  if (protractor.dragging) {
    // Only the finger that grabbed the point moves it — every move is tagged
    // with its pointer id so a second one cannot drive the drag.
    protractor.dragTo(e.clientX, e.clientY, e.pointerId);
    updatePhotoReadout();
    return;
  }
  // Desktop affordance: a grab cursor when the pointer is over a point.
  refs.photoCanvas.classList.toggle('over-point', protractor.isOverPoint(e.clientX, e.clientY));
});
const endPhotoDrag = (e: PointerEvent): void => {
  protractor.endDrag(e.pointerId);
  if (!protractor.dragging) refs.photoCanvas.classList.remove('dragging');
};
refs.photoCanvas.addEventListener('pointerup', endPhotoDrag);
refs.photoCanvas.addEventListener('pointercancel', endPhotoDrag);
// The grab cursor would otherwise stick once the mouse has left the canvas.
refs.photoCanvas.addEventListener('pointerleave', () => {
  refs.photoCanvas.classList.remove('over-point');
});
refs.photoUndoBtn.addEventListener('click', () => {
  protractor.undo();
  updatePhotoReadout();
});
refs.photoClearBtn.addEventListener('click', () => {
  protractor.clearPoints();
  updatePhotoReadout();
});
refs.photoCaptureBtn.addEventListener('click', capturePhoto);
refs.photoPngBtn.addEventListener('click', async () => {
  const png = await protractor.toPng();
  if (png) {
    downloadBlob(png, `tiltwell-photo-${stamp()}.png`);
    log.export('Annotated photo downloaded.', 'ok');
  } else {
    showToast('Load a photo first.');
  }
});
refs.photoPdfBtn.addEventListener('click', async () => {
  const pdf = await getPhotoPdf();
  if (pdf) {
    downloadBlob(pdf, `tiltwell-photo-${stamp()}.pdf`);
    log.export('Annotated photo PDF downloaded.', 'ok');
  } else {
    showToast('Load a photo first.');
  }
});
refs.photoShareBtn.addEventListener('click', async () => {
  const png = await protractor.toPng();
  if (!png) {
    showToast('Load a photo first.');
    return;
  }
  const file = new File([png], `tiltwell-photo-${stamp()}.png`, { type: 'image/png' });
  if (await shareFile(file, 'Tiltwell annotated photo')) {
    log.export('Shared the annotated photo.', 'ok');
  }
});

/**
 * The annotated photo as a one-page PDF: the same full-resolution render the PNG
 * export uses, as a JPEG embedded verbatim, plus the reading written as text.
 */
async function getPhotoPdf(): Promise<Blob | null> {
  const size = protractor.imageSize();
  if (!size) return null;
  const jpeg = await protractor.toJpeg();
  if (!jpeg) return null;
  const lines = [protractor.measurement() ? photoReadoutText() : 'No points marked yet.', `Measured ${dateStamp()}`];
  try {
    const bytes = buildPdfBytes({
      jpeg: new Uint8Array(await jpeg.arrayBuffer()),
      imgW: size.w,
      imgH: size.h,
      title: 'Tiltwell — annotated photo',
      lines,
      footer: 'Measured on-device with tiltwell.benrichardson.dev. Nothing was uploaded.',
    });
    return new Blob([bytes], { type: 'application/pdf' });
  } catch {
    log.export('Could not build the PDF.', 'err');
    return null;
  }
}

async function loadPhoto(file: File): Promise<void> {
  if (!file.type.startsWith('image/')) {
    showToast('That is not an image file.');
    return;
  }
  if (file.size > 40 * 1024 * 1024) {
    showToast('That image is very large (>40 MB).');
    return;
  }
  try {
    log.photo(`Loading ${file.name}…`);
    await protractor.load(file);
    refs.photoStage.hidden = false;
    refs.photoReadout.hidden = false;
    refs.photoHint.hidden = false;
    document.getElementById('photo-controls')!.hidden = false;
    refs.photoShareBtn.hidden = !('share' in navigator && 'canShare' in navigator);
    // load() drew while the stage was still hidden, so the canvas holds a
    // zero-size bitmap the browser is stretching. Redraw now it has a real size.
    protractor.draw();
    updatePhotoReadout();
    log.photo('Photo loaded — tap points to measure.', 'ok');
  } catch (err) {
    log.photo(`Could not load that image: ${(err as Error)?.message ?? err}`, 'err');
    showToast('Could not load that image.');
  }
}

// Fetch the bundled demo photo and feed it through the SAME path a real upload
// takes (loadPhoto), so pressing "Try a sample" behaves exactly like a drop.
async function loadSample(): Promise<void> {
  try {
    log.photo('Loading a sample photo…');
    const res = await fetch('samples/ramp.jpg');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    await loadPhoto(new File([blob], 'ramp.jpg', { type: blob.type || 'image/jpeg' }));
  } catch (err) {
    log.photo(`Could not load the sample: ${(err as Error)?.message ?? err}`, 'err');
    showToast('Could not load the sample.');
  }
}

// One source of truth for the photo reading — shown on screen and written into
// the PDF export, so the file says exactly what the user was looking at.
function photoReadoutText(): string {
  const info = protractor.measurement();
  if (!info) {
    return protractor.pointCount === 1 ? 'One point — tap another.' : 'Tap points on the photo.';
  }
  if (info.kind === 'slope' && info.angle <= 89.9) {
    return `Slope ${formatDeg(info.angle)} · ${formatGrade(degToGrade(info.angle))} · ${roofPitchLabel(info.angle)} · ${ratioLabel(info.angle)}`;
  }
  if (info.kind === 'slope') {
    return `Slope ${formatDeg(info.angle)} (near vertical)`;
  }
  return `Angle ${formatDeg(info.angle)} at the vertex`;
}

function updatePhotoReadout(): void {
  refs.photoReadout.textContent = photoReadoutText();
}

// Captures exports
refs.dlCsvBtn.addEventListener('click', () => {
  if (!measurements.length) {
    showToast('No measurements to export.');
    return;
  }
  downloadBlob(new Blob([toCsv(measurements)], { type: 'text/csv' }), `tiltwell-${stamp()}.csv`);
  log.export('Measurements CSV downloaded.', 'ok');
});
refs.dlPngBtn.addEventListener('click', async () => {
  const png = await getReportPng();
  if (png) {
    downloadBlob(png, `tiltwell-${stamp()}.png`);
    log.export('Report image downloaded.', 'ok');
  } else {
    showToast('Capture a measurement first.');
  }
});
refs.copyPngBtn.addEventListener('click', async () => {
  const png = await getReportPng();
  if (!png) {
    showToast('Capture a measurement first.');
    return;
  }
  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    showToast('Report image copied.');
    log.export('Report image copied.', 'ok');
  } catch {
    showToast('Copy not supported — use Download.');
  }
});
refs.sharePngBtn.addEventListener('click', async () => {
  const png = await getReportPng();
  if (!png) return;
  const file = new File([png], `tiltwell-${stamp()}.png`, { type: 'image/png' });
  if (await shareFile(file, 'Tiltwell measurements')) {
    log.export('Shared the report image.', 'ok');
  }
});
refs.copySummaryBtn.addEventListener('click', async () => {
  if (!measurements.length) {
    showToast('No measurement to summarise.');
    return;
  }
  try {
    await navigator.clipboard.writeText(summaryLine(measurements[0]));
    showToast('Summary copied.');
    log.export('Summary copied.', 'ok');
  } catch {
    showToast('Copy not supported.');
  }
});
refs.clearCapsBtn.addEventListener('click', () => {
  measurements = [];
  renderCaptureList();
  showToast('All measurements cleared.');
  log.capture('Cleared all measurements.');
});

// ── Event drawer ─────────────────────────────────────────────────────

let drawerOpen = false;
function toggleDrawer(force?: boolean): void {
  drawerOpen = force ?? !drawerOpen;
  refs.drawer.hidden = !drawerOpen;
  refs.drawer.setAttribute('aria-hidden', String(!drawerOpen));
  refs.drawerToggle.setAttribute('aria-expanded', String(drawerOpen));
  const scrim = document.getElementById('scrim')!;
  if (drawerOpen) {
    scrim.hidden = false;
    scrim.onclick = () => toggleDrawer(false);
  } else if (!isModalOpen(root)) {
    scrim.hidden = true;
  }
}
mountEventDrawer(refs.drawerBody, () => toggleDrawer(false));
refs.drawerToggle.addEventListener('click', () => toggleDrawer());

// ── Keyboard ─────────────────────────────────────────────────────────

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    hideGlossary();
    if (isModalOpen(root)) closeModal(root);
    else if (drawerOpen) toggleDrawer(false);
    return;
  }
  if (isTyping(e.target) || isModalOpen(root)) return;
  if (e.key === ' ') {
    e.preventDefault();
    if (mode === 'photo') capturePhoto();
    else if (measuring) captureCurrent();
    else void startMeasuring();
  } else if ((e.key === 'c' || e.key === 'C') && measuring) {
    calibrate();
  }
});

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

// Keep the canvases correctly sized on rotation/resize — the gauge redraws even
// at idle so a desktop shows a proper empty bubble level rather than a blank box.
window.addEventListener('resize', () => {
  if (mode === 'photo') {
    if (protractor.hasImage()) protractor.draw();
  } else {
    renderLive();
  }
});

setModalOpenHook((id) => log.system(`Opened “${id}”.`));

// ── Boot ─────────────────────────────────────────────────────────────

// Reflect the persisted mode without an early return in setMode.
if (mode !== 'level') {
  const start = mode;
  mode = 'level';
  setMode(start);
} else {
  refs.tabs.querySelector<HTMLElement>('.tab[data-mode="level"]')?.classList.add('active');
  renderLive();
}

// Draw the initial gauge once layout has settled (a synchronous first paint can
// measure the canvas at 0px). setTimeout, not rAF — a hidden/preview tab throttles
// rAF to a halt but still fires timers.
setTimeout(() => {
  if (mode !== 'photo') renderLive();
}, 60);

log.system('Tiltwell ready. Everything runs in your browser.', 'ok');
if (!isOrientationSupported()) {
  log.system('No orientation sensor detected — the Photo tab is your path in.', 'info');
}
// The service worker (offline support) is generated and auto-registered by
// vite-plugin-pwa — see vite.config.ts. No manual registration here.

// A tiny test hook so the browser dry-run can push synthetic orientation samples
// (the automation cannot tilt a real phone). Guarded and side-effect-free.
interface TestHook {
  setMode(m: MeasureMode): void;
  feedGravity(v: Vec3): void;
  capture(): void;
  loadPhoto(blob: Blob, name?: string): Promise<void>;
  addPhotoPoint(clientX: number, clientY: number): void;
  state(): { mode: MeasureMode; measuring: boolean; count: number; angle: number | null };
}
(window as unknown as { __tiltwell?: TestHook }).__tiltwell = {
  setMode,
  feedGravity(v: Vec3) {
    if (!measuring) {
      measuring = true;
      setMeasuringUi(true);
      startRenderLoop();
    }
    latestGravity = v;
    renderLive();
  },
  capture: captureCurrent,
  loadPhoto: (blob: Blob, name = 'test.png') => loadPhoto(new File([blob], name, { type: blob.type || 'image/png' })),
  addPhotoPoint(clientX: number, clientY: number) {
    protractor.addPointFromClient(clientX, clientY);
    updatePhotoReadout();
  },
  state() {
    const t = currentTilt();
    return { mode, measuring, count: measurements.length, angle: t ? primaryAngle(t) : null };
  },
};
